import styled from "@emotion/styled";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { LeftArrowIcon, RightArrowIcon } from "../../assets/icons/icons";
import {
  DetectByMonth,
  DetectByDay as PropsType,
} from "../../types/types";
import theme from "../../theme";
import { Button } from "../../components/common/Button";
import { useWindowSize } from "@reactuses/core";
import { SrLable } from "../../components/common/SrLabel";
import { useI18n } from "../../locales";

const { md } = theme.screen;

/**
 * 조회 기간이 90일 미만이면 일별 조회
 * 조회 기간이 90일 이상이면 월별 조회
 *
 * 조회 후 일별/월별 조회로 토글 가능
 */

const DetectByDay: React.FC<{
  dayData: PropsType[] | undefined;
  monthData: DetectByMonth[] | undefined;
  diffDate: number;
}> = (props) => {
  const { t } = useI18n();
  const { dayData, monthData, diffDate } = props;
  const { width } = useWindowSize();
  const [count, setCount] = useState(0);
  const [showMonth, setShowMonth] = useState(false);

  const increase = () => {
    setCount((prev) => {
      if (prev + 30 === dayData?.length) {
        return prev;
      } else {
        return prev + 1;
      }
    });
  };
  const decrease = () => {
    setCount((prev) => {
      if (prev === 0) return prev;
      else return prev - 1;
    });
  };

  useEffect(() => {
    if (diffDate < 90) {
      setShowMonth(false);
      return;
    }
    if (diffDate > 89) {
      setShowMonth(true);
      return;
    }
  }, [diffDate]);
  // console.log(monthData);
  if (dayData && !showMonth) {
    return (
      <Wrapper>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Header>{t.statistics.dailyDetections}</Header>

          {!showMonth && (
            <Button
              bgColor={theme.statusColor.MAIN}
              hoverColor={theme.statusColor.MAIN_HOVER}
              onClick={() => setShowMonth(true)}
              height={width > md ? "32px" : "24px"}
              width={width > md ? "90px" : "70px"}
              paddingX="0px"
            >
              {t.statistics.monthView}
            </Button>
          )}
        </div>
        <Body>
          <Chart
            height="100%"
            type="line"
            series={[
              {
                name: t.statistics.detectionHistory,
                data: dayData
                  ?.slice(count, 30 + count)
                  .map((el) => el.cnt) as number[],
              },
            ]}
            options={{
              chart: {
                type: "line",
                toolbar: {
                  show: false,
                },
                zoom: {
                  enabled: false,
                },
              },
              markers: {
                size: 5,
                colors: theme.color.white,
                strokeColors: "#888CAD",
                hover: {
                  size: undefined,
                  sizeOffset: 0,
                },
              },
              dataLabels: {
                enabled: false,
              },
              stroke: {
                curve: "straight",
                width: 2,
                colors: [theme.statusColor.MAIN],
              },

              xaxis: {
                categories: dayData?.slice(count, 30 + count).map((el) => {
                  const date = new Date(el.day);
                  const dayOfWeek = t.statistics.dayOfWeek[date.getDay()];
                  return format(date, "MM/dd") + `(${dayOfWeek})`;
                }),
                axisBorder: {
                  show: false,
                },
              },
            }}
          />
        </Body>
        {/* 데이터 30개 이상인 경우 활성화되는 버튼과 슬라이더 */}
        {typeof dayData !== "undefined" && dayData?.length > 30 && (
          <div
            style={{
              padding: width > md ? "0 10px" : "0px",
              width: "100%",
              display: "flex",
              gap: width > md ? "15px" : "10px",
              justifySelf: "center",
              alignSelf: "center",
              marginTop: width > md ? "5px" : "0px",
            }}
          >
            <SliderIconWrapper onClick={decrease}>
              <LeftArrowIcon
                width={width <= md ? 18 : undefined}
                height={width <= md ? 18 : undefined}
              />
            </SliderIconWrapper>

            <RangeInputContainer>
              <SrLable labelText={t.mobileList.title} id="range" />
              <RangeInput
                type="range"
                name=""
                id="range"
                min={0}
                max={dayData.length - 30}
                step={1}
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value))}
              />
            </RangeInputContainer>

            <SliderIconWrapper onClick={increase}>
              <RightArrowIcon
                width={width <= md ? 18 : undefined}
                height={width <= md ? 18 : undefined}
              />
            </SliderIconWrapper>
          </div>
        )}
        {typeof dayData !== "undefined" && dayData?.length > 30 && (
          <div
            style={{
              paddingRight: "5px",
              color: "#737374",
              textAlign: "right",
              paddingTop: "5px",
              fontSize: width > md ? "14px" : "10px",
            }}
          >{`페이지: ${count + 1} / ${dayData.length - 29}`}</div>
        )}
      </Wrapper>
    );
  } else if (monthData && showMonth) {
    return (
      <Wrapper>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Header>{t.statistics.monthlyDetections}</Header>
          {showMonth && (
            <Button
              bgColor={theme.statusColor.MAIN}
              hoverColor={theme.statusColor.MAIN_HOVER}
              onClick={() => setShowMonth(false)}
              height={width > md ? "32px" : "24px"}
              width={width > md ? "90px" : "70px"}
              paddingX="0px"
            >
              {t.statistics.dailyView}
            </Button>
          )}
        </div>
        <Body>
          <Chart
            height="100%"
            series={[
              {
                name: t.statistics.detectionHistory,
                data: monthData?.map((el) => el.cnt) as number[],
              },
            ]}
            options={{
              chart: {
                type: "line",
                toolbar: {
                  show: false,
                },
                zoom: {
                  enabled: false,
                },
              },
              markers: {
                size: 5,
                colors: "#FFFFFF",
                strokeColors: "#888CAD",
                hover: {
                  size: undefined,
                  sizeOffset: 0,
                },
              },
              dataLabels: {
                enabled: false,
              },
              stroke: {
                curve: "straight",
                width: 2,
                colors: [theme.color.green_500],
              },

              xaxis: {
                categories: monthData?.map((el) => {
                  return `${el.month.slice(0, 4)}년 ${el.month.slice(
                    5
                  )}월`;
                }),

                axisBorder: {
                  show: false,
                },
              },
            }}
          />
        </Body>
      </Wrapper>
    );
  } else {
    return <></>;
  }
};

const Wrapper = styled.div`
  width: 1098px;
  background: #f9f9f9;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.25);
  border-radius: 15px;
  /* height: 100%; */
  height: 531px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media (max-width: ${md}px) {
    border-radius: 5px;
    background: ${theme.color.white};
    width: 100%;
    box-shadow: none;
    height: auto;
    min-height: 350px;
    padding: 10px;

    tspan {
      font-size: ${theme.fontSize.CAPTION_1};
    }
  }
`;

const Header = styled.h1`
  font-weight: 700;
  font-size: 15px;
  line-height: 18px;
  color: #484848;
  margin-bottom: 20px;

  @media (max-width: ${md}px) {
    font-size: ${theme.fontSize.FONT_1};
    font-weight: ${theme.fontWeight.SEMI_BOLD};
    margin-bottom: 12px;
  }
`;

const Body = styled.div`
  flex: 1;
  background: #fefefe;
  border-radius: 20px;
  height: 303px;

  @media (max-width: ${md}px) {
    height: 100%;
  }
`;

const RangeInputContainer = styled.div`
  display: flex;
  flex-shrink: 1;
  flex-grow: 1;
`;

const RangeInput = styled.input`
  appearance: none;
  padding-top: 2px;
  width: 100%;
  height: 20px;
  cursor: pointer;
  accent-color: ${theme.color.white};

  &::-webkit-slider-runnable-track {
    background: ${theme.statusColor.MAIN};
    border-radius: 25px;
    height: 18px;

    @media (max-width: ${md}px) {
      height: 14px;
    }
  }
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    position: relative;
    transform: translateY(-20%);
    border: 0px solid #fff;
    width: 30px;
    height: 30px;
    border-radius: 25px;
    background: white;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.25);

    @media (max-width: ${md}px) {
      width: 20px;
      height: 20px;
    }
  }

  @media (max-width: ${md}px) {
    font-size: ${theme.fontSize.FONT_3};
  }
`;

const SliderIconWrapper = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default DetectByDay;
